import React, { useEffect, useMemo, useState, useRef } from 'react';
import CountryFlag from 'react-country-flag';
import './Odds.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Favorite's decimal odds and the corresponding american-style odds, e.g. "1.50 (-200)"
const formatDecimalAndAmerican = (decimal) => {
    const american = decimal >= 2 ? (decimal - 1) * 100 : -100 / (decimal - 1);
    return `${decimal.toFixed(2)} (${american > 0 ? '+' : ''}${Math.round(american)})`;
};

// Adjusted odds: derived from the win probabilities (bookmaker fee removed + time correction)
const formatAdjustedFavoriteOdds = (fight) =>
    formatDecimalAndAmerican(1 / Math.max(parseFloat(fight.fighter1_odds), parseFloat(fight.fighter2_odds)));

// Raw odds: straight from the bookmaker columns. The favorite is the smaller of the two,
// and often only the favorite's value is recorded, so use whichever ones are available.
const formatRawFavoriteOdds = (fight) => {
    const available = [fight.fighter1_raw_odds, fight.fighter2_raw_odds].filter(o => o != null && o > 0);
    return available.length ? formatDecimalAndAmerican(Math.min(...available)) : '–';
};

// Raw favorite: the smaller of the available raw odds (often only the favorite's is recorded)
const getRawFavorite = (fight) => {
    const o1 = fight.fighter1_raw_odds > 0 ? fight.fighter1_raw_odds : null;
    const o2 = fight.fighter2_raw_odds > 0 ? fight.fighter2_raw_odds : null;
    if (o1 === null && o2 === null) return null;
    return {
        odds: Math.min(...[o1, o2].filter(o => o !== null)),
        isFighter1Favorite: o1 !== null && (o2 === null || o1 <= o2)
    };
};

function Odds() {
    const [fights, setFights] = useState([]);
    const [upsets, setUpsets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [minYear, setMinYear] = useState('2012');
    const [maxYear, setMaxYear] = useState('');
    const [titleFightsOnly, setTitleFightsOnly] = useState(false);
    const [appliedMinYear, setAppliedMinYear] = useState('2012');
    const [appliedMaxYear, setAppliedMaxYear] = useState('');
    const [appliedTitleFightsOnly, setAppliedTitleFightsOnly] = useState(false);
    const [fetchTrigger, setFetchTrigger] = useState(0);
    const [oddsMode, setOddsMode] = useState('adjusted');
    const mismatchesRequestIdRef = useRef(0);
    const upsetRequestIdRef = useRef(0);

    useEffect(() => {
        fetchBiggestMismatches(appliedMinYear, appliedMaxYear, appliedTitleFightsOnly);
        fetchBiggestUpsets(appliedMinYear, appliedMaxYear, appliedTitleFightsOnly);
    }, [appliedMinYear, appliedMaxYear, appliedTitleFightsOnly, fetchTrigger]);

    const fetchBiggestMismatches = async (minYear, maxYear, onlyTitleFights) => {
        const requestId = ++mismatchesRequestIdRef.current;
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (minYear) params.append('minYear', minYear);
            params.append('maxYear', maxYear || 2026);
            if (onlyTitleFights) params.append('titleFightFilter', 'title');
            const res = await fetch(`${API_BASE}/api/biggest-mismatches?${params.toString()}`);
            if (requestId !== mismatchesRequestIdRef.current) return; // Ignore stale response
            if (!res.ok) throw new Error('Failed to fetch biggest mismatches');
            const data = await res.json();
            if (requestId !== mismatchesRequestIdRef.current) return; // Ignore stale response
            
            // Deduplicate by fighter1, fighter2, event_date
            const seen = new Set();
            const dedupedData = data.filter(fight => {
                const key = `${fight.fighter1}|${fight.fighter2}|${fight.event_date}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
            
            setFights(dedupedData);
            if (!minYear && dedupedData.length > 0) {
                const years = dedupedData.map(f => new Date(f.event_date).getFullYear()).filter(y => !isNaN(y));
                if (years.length > 0) setMinYear(String(Math.min(...years)));
            }
        } catch (err) {
            if (requestId !== mismatchesRequestIdRef.current) return; // Ignore stale response
            console.error('Error fetching mismatches:', err);
            setError(err.message);
        } finally {
            if (requestId === mismatchesRequestIdRef.current) setLoading(false);
        }
    };

    const fetchBiggestUpsets = async (minYear, maxYear, onlyTitleFights) => {
        const requestId = ++upsetRequestIdRef.current;
        try {
            const params = new URLSearchParams();
            if (minYear) params.append('minYear', minYear);
            params.append('maxYear', maxYear || 2026);
            if (onlyTitleFights) params.append('titleFightFilter', 'title');
            const res = await fetch(`${API_BASE}/api/biggest-upsets?${params.toString()}`);
            if (requestId !== upsetRequestIdRef.current) return; // Ignore stale response
            if (!res.ok) throw new Error('Failed to fetch biggest upsets');
            const data = await res.json();
            if (requestId !== upsetRequestIdRef.current) return; // Ignore stale response
            
            // Deduplicate by id (unique fight identifier)
            const seen = new Set();
            const dedupedData = data.filter(fight => {
                if (seen.has(fight.id)) return false;
                seen.add(fight.id);
                return true;
            });
            
            console.log(`[upsets] Original: ${data.length}, Deduplicated: ${dedupedData.length}`);
            setUpsets(dedupedData);
        } catch (err) {
            if (requestId !== upsetRequestIdRef.current) return; // Ignore stale response
            console.error('Error fetching upsets:', err);
        }
    };

    const styles = {
        container: {
            maxWidth: '950px',
            margin: '0 auto',
            padding: '2rem'
        },
        title: {
            fontSize: '1.75rem',
            fontWeight: 600,
            marginBottom: '2rem',
            color: '#fff'
        },
        tableContainer: {
            overflowX: 'auto',
            border: '1px solid #2d3748',
            borderRadius: '8px',
            background: 'rgba(255, 255, 255, 0.02)'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.9rem',
            tableLayout: 'auto'
        },
        th: {
            padding: '0.3rem 0.5rem',
            textAlign: 'left',
            borderBottom: '2px solid #2d3748',
            fontWeight: 600,
            color: '#e0e0e0',
            background: 'rgba(0, 0, 0, 0.2)',
            fontSize: '0.8rem',
            whiteSpace: 'nowrap'
        },
        td: {
            padding: '0.25rem 0.5rem',
            borderBottom: '1px solid #1a202c',
            color: '#ccc',
            fontSize: '0.8rem',
            whiteSpace: 'nowrap'
        },
        loading: {
            textAlign: 'center',
            padding: '2rem',
            color: '#888'
        },
        error: {
            textAlign: 'center',
            padding: '2rem',
            color: '#ff6b6b'
        },
        mismatchValue: {
            fontWeight: 600,
            color: '#ff9f43'
        },
        rowHighlight: (index) => ({
            background: index % 2 === 0 ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
            transition: 'background 0.2s ease'
        })
    };

    const calculateRanks = (data, rankField = 'mismatch_score') => {
        const ranks = [];
        let currentRank = 1;
        for (let i = 0; i < data.length; i++) {
            if (i > 0 && data[i][rankField] !== data[i - 1][rankField]) {
                currentRank = i + 1;
            }
            ranks.push(currentRank);
        }
        return ranks;
    };

    const formatFavoriteOdds = oddsMode === 'raw' ? formatRawFavoriteOdds : formatAdjustedFavoriteOdds;

    // In raw mode, rank and pick favorites from the raw odds. The mismatches list holds every fight
    // (with its winner), so the raw upsets are derived from it: fights the raw favorite lost.
    const { shownFights, shownUpsets } = useMemo(() => {
        if (oddsMode !== 'raw') return { shownFights: fights, shownUpsets: upsets };
        const rawFights = fights
            .map(f => ({ ...f, rawFavorite: getRawFavorite(f) }))
            .filter(f => f.rawFavorite)
            .map(f => ({ ...f, mismatch_score: f.rawFavorite.odds, isFighter1Favorite: f.rawFavorite.isFighter1Favorite }))
            .sort((a, b) =>
                a.mismatch_score - b.mismatch_score ||
                (a.event_date < b.event_date ? 1 : a.event_date > b.event_date ? -1 : 0) ||
                a.id - b.id);
        const rawUpsets = rawFights.filter(f =>
            f.fight_winner && f.fight_winner === (f.isFighter1Favorite ? f.fighter2 : f.fighter1));
        return { shownFights: rawFights, shownUpsets: rawUpsets };
    }, [oddsMode, fights, upsets]);

    const getFlagCode = (country) => {
        if (!country) return null;
        const code = String(country).toUpperCase();
        if (code === 'UK') return 'GB';
        if (code.startsWith('RU-')) return 'RU';
        return code;
    };

    const renderFighterWithFlag = (name, country) => {
        const flagCode = getFlagCode(country);
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                {flagCode && <CountryFlag countryCode={flagCode} svg style={{ width: '1.1em', height: '1.1em' }} />}
                <span>{name}</span>
            </div>
        );
    };

    if (loading) {
        return <div style={styles.loading}>Loading biggest mismatches...</div>;
    }

    if (error) {
        return <div style={styles.error}>Error: {error}</div>;
    }

    return (
        <div style={styles.container}>
            <div className="year-slider-container" style={{ marginBottom: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', marginBottom: '1rem' }}>
                    <div className="year-range-item">
                        <div className="year-range-label">Min Year</div>
                        <input
                            type="number"
                            min="1990"
                            max="2026"
                            value={minYear}
                            onChange={(e) => setMinYear(e.target.value)}
                            style={{
                                padding: '0.3rem 0.5rem',
                                fontSize: '0.9rem',
                                borderRadius: '4px',
                                border: '1px solid #555',
                                background: '#1a1a1a',
                                color: '#fff',
                                width: '80px'
                            }}
                        />
                    </div>
                    <div className="year-range-item">
                        <div className="year-range-label">Max Year</div>
                        <input
                            type="number"
                            min="1990"
                            max="2026"
                            value={maxYear}
                            onChange={(e) => setMaxYear(e.target.value)}
                            style={{
                                padding: '0.3rem 0.5rem',
                                fontSize: '0.9rem',
                                borderRadius: '4px',
                                border: '1px solid #555',
                                background: '#1a1a1a',
                                color: '#fff',
                                width: '80px'
                            }}
                        />
                    </div>
                    <label
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            color: '#fff',
                            fontSize: '0.9rem',
                            cursor: 'pointer',
                            paddingBottom: '0.3rem'
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={titleFightsOnly}
                            onChange={(e) => setTitleFightsOnly(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                        />
                        Title fights only
                    </label>
                    <div className="year-range-item">
                        <div className="year-range-label">Odds</div>
                        <div style={{ display: 'flex' }}>
                            {[
                                ['adjusted', 'Adjusted (fee + time correction)'],
                                ['raw', 'Raw']
                            ].map(([mode, label], i) => (
                                <button
                                    key={mode}
                                    onClick={() => setOddsMode(mode)}
                                    style={{
                                        padding: '0.3rem 0.75rem',
                                        fontSize: '0.9rem',
                                        border: '1px solid #555',
                                        borderLeft: i === 0 ? '1px solid #555' : 'none',
                                        borderRadius: i === 0 ? '4px 0 0 4px' : '0 4px 4px 0',
                                        background: oddsMode === mode ? '#3a4a66' : '#1a1a1a',
                                        color: oddsMode === mode ? '#fff' : '#aaa',
                                        cursor: 'pointer',
                                        transition: 'background 0.2s ease'
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            setAppliedMinYear(minYear || 1990);
                            setAppliedMaxYear(maxYear || 2026);
                            setAppliedTitleFightsOnly(titleFightsOnly);
                            setFetchTrigger(t => t + 1);
                        }}
                        style={{
                            padding: '0.3rem 1rem',
                            backgroundColor: '#333',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            transition: 'background 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#444'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#333'}
                    >
                        Apply
                    </button>
                </div>
            </div>

            <h1 style={styles.title}>UFC Biggest {appliedTitleFightsOnly ? 'Title Fight ' : ''}Mismatches ({appliedMinYear}–{appliedMaxYear || 2026})</h1>
            <div style={styles.tableContainer}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}></th>
                            <th style={styles.th}>Favorite</th>
                            <th style={styles.th}>Underdog</th>
                            <th style={styles.th}>Favorite odds*</th>
                            <th style={styles.th}>Winner</th>
                            <th style={styles.th}>Event</th>
                            <th style={styles.th}>Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(() => {
                            const fightsRanks = calculateRanks(shownFights);
                            return shownFights.slice(0, 20).map((fight, index) => {
                                const isFighter1Favorite = fight.isFighter1Favorite ?? fight.fighter1_odds > fight.fighter2_odds;
                                const favorite = isFighter1Favorite ? fight.fighter1 : fight.fighter2;
                                const underdog = isFighter1Favorite ? fight.fighter2 : fight.fighter1;
                                const favoriteCountry = isFighter1Favorite ? fight.fighter1_country : fight.fighter2_country;
                                const underdogCountry = isFighter1Favorite ? fight.fighter2_country : fight.fighter1_country;
                                const isDraw = !fight.fight_winner || fight.fight_winner === '';
                                
                                return (
                                    <tr key={fight.id} style={styles.rowHighlight(index)}>
                                        <td style={{...styles.td, fontWeight: isDraw ? '700' : '400', color: isDraw ? '#ff6b6b' : '#ccc'}}>
                                            {isDraw ? 'TIE' : (index > 0 && fightsRanks[index] === fightsRanks[index - 1] ? '–' : fightsRanks[index])}
                                        </td>
                                        <td style={styles.td}>{renderFighterWithFlag(favorite, favoriteCountry)}</td>
                                        <td style={styles.td}>{renderFighterWithFlag(underdog, underdogCountry)}</td>
                                        <td style={styles.td}>
                                            {formatFavoriteOdds(fight)}
                                        </td>
                                        <td style={styles.td}>
                                            {fight.fight_winner === favorite ? 'Favorite' : fight.fight_winner === underdog ? 'Underdog' : 'Draw/NC'}
                                        </td>
                                        <td style={styles.td}>
                                            {fight.event_url ? (
                                                <a href={fight.event_url} target="_blank" rel="noopener noreferrer" style={{ color: '#ccc', textDecoration: 'none', transition: 'color 0.2s ease' }} onMouseEnter={(e) => e.target.style.color = '#6ba3ff'} onMouseLeave={(e) => e.target.style.color = '#ccc'}>
                                                    {fight.event_name}
                                                </a>
                                            ) : (
                                                fight.event_name
                                            )}
                                        </td>
                                        <td style={styles.td}>{fight.event_date}</td>
                                    </tr>
                                );
                            });
                        })()}
                    </tbody>
                </table>
            </div>

            <h1 style={{...styles.title, marginTop: '4rem'}}>UFC Biggest {appliedTitleFightsOnly ? 'Title Fight ' : ''}Upsets ({appliedMinYear}–{appliedMaxYear || 2026})</h1>
            <div style={styles.tableContainer}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}></th>
                            <th style={styles.th}>Favorite</th>
                            <th style={styles.th}>Underdog</th>
                            <th style={styles.th}>Favorite odds*</th>
                            <th style={styles.th}>Event</th>
                            <th style={styles.th}>Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(() => {
                            const upsetRanks = calculateRanks(shownUpsets);
                            return shownUpsets.slice(0, 20).map((upset, index) => {
                                const isFighter1Favorite = upset.isFighter1Favorite ?? upset.fighter1_odds > upset.fighter2_odds;
                                const favorite = isFighter1Favorite ? upset.fighter1 : upset.fighter2;
                                const underdog = isFighter1Favorite ? upset.fighter2 : upset.fighter1;
                                const favoriteCountry = isFighter1Favorite ? upset.fighter1_country : upset.fighter2_country;
                                const underdogCountry = isFighter1Favorite ? upset.fighter2_country : upset.fighter1_country;
                                const isDraw = !upset.fight_winner || upset.fight_winner === '';
                                
                                return (
                                    <tr key={upset.id} style={styles.rowHighlight(index)}>
                                        <td style={{...styles.td, fontWeight: isDraw ? '700' : '400', color: isDraw ? '#ff6b6b' : '#ccc'}}>
                                            {isDraw ? 'TIE' : (index > 0 && upsetRanks[index] === upsetRanks[index - 1] ? '–' : upsetRanks[index])}
                                        </td>
                                        <td style={styles.td}>{renderFighterWithFlag(favorite, favoriteCountry)}</td>
                                        <td style={styles.td}>{renderFighterWithFlag(underdog, underdogCountry)}</td>
                                        <td style={styles.td}>
                                            {formatFavoriteOdds(upset)}
                                        </td>
                                        <td style={styles.td}>
                                            {upset.event_url ? (
                                                <a href={upset.event_url} target="_blank" rel="noopener noreferrer" style={{ color: '#ccc', textDecoration: 'none', transition: 'color 0.2s ease' }} onMouseEnter={(e) => e.target.style.color = '#6ba3ff'} onMouseLeave={(e) => e.target.style.color = '#ccc'}>
                                                    {upset.event_name}
                                                </a>
                                            ) : (
                                                upset.event_name
                                            )}
                                        </td>
                                        <td style={styles.td}>{upset.event_date}</td>
                                    </tr>
                                );
                            });
                        })()}
                    </tbody>
                </table>
            </div>

            <div style={{ marginTop: '2rem', fontSize: '0.75rem', fontStyle: 'italic', color: '#999', lineHeight: '1.6', borderTop: '1px solid #2d3748', paddingTop: '1.5rem' }}>
                <p style={{ margin: '0 0 0.75rem 0' }}>
                    * Favorite's decimal closing odds (american-style odds between brackets). Adjusted odds are averaged over several bookmakers, recomputed to assume no fee and corrected over time (see **); raw odds are the bookmaker values as recorded, with the favorite being the smaller of the two. Raw data from{' '}
                    <a href="https://fightodds.io" target="_blank" rel="noopener noreferrer" style={{ color: '#6fb3e0', textDecoration: 'none', borderBottom: '1px solid #6fb3e0' }}>fightodds.io</a>
                    . All of them are manually curated: reported (closing) values are sometimes (and unpredictably) from a later time than the start of the fight. Sometimes, for other unknown reasons, they can also be completely nonsensical given values from the other bookmakers. This holds for all available bookmakers so there isn't even a subset of reliable ones whose values can be brute-force extracted and averaged.
                </p>
                <p style={{ margin: '0' }}>
                    ** As time passed, distribution of odds became more tail-heavy (stabilized around covid). It's arguable but we can suspect it is not due to matchmaking becoming less balanced, but to the bookmakers' algorithms getting more accurate (in part thanks to more data) and more confident. To account for that I applied a time-dependent continuous transformation such that every year roughly has the same distribution.
                </p>
            </div>
        </div>
    );
}

export default Odds;
